import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { CalendarView } from 'angular-calendar';

import {
  addEvent,
  addEventFailure,
  addEventSuccess,
  fetchEvents,
  fetchEventsFailure,
  fetchEventsSuccess,
  fetchNewEventCanHaveASecondMeetingDate,
  fetchNewEventCanHaveASecondMeetingDateFailure,
  fetchNewEventCanHaveASecondMeetingDateSuccess,
  fetchNewEventOrgans,
  fetchNewEventOrgansFailure,
  fetchNewEventOrgansSuccess,
  fetchOrgans,
  fetchOrgansFailure,
  fetchOrgansSuccess,
  fetchUnscheduledEvents,
  fetchUnscheduledEventsFailure,
  fetchUnscheduledEventsSuccess,
  fetchUserCanCreateEvent,
  fetchUserCanCreateEventFailure,
  fetchUserCanCreateEventSuccess,
  fetchUserCanSeeUnscheduledEvents,
  fetchUserCanSeeUnscheduledEventsFailure,
  fetchUserCanSeeUnscheduledEventsSuccess,
  removeEvents,
  removeEventsFailure,
  removeEventsSuccess,
  updateSelectedOrgansFailure,
  updateSelectedOrgansSuccess
} from './calendar.actions';
import { Event, NewEventOrgan, Organ, UnscheduledEvents } from './calendar.models';

export interface CalendarState extends EntityState<Event> {
  error: any;
  loadingEvents: boolean;
  loadingUnscheduledEvents: boolean;
  loadingOrgans: boolean;
  loadingNewEventOrgans: boolean;
  loadingAddEvent: boolean;
  loadingRemoveEvents: boolean;
  organs: Organ[];
  newEventOrgans: NewEventOrgan[];
  unscheduledEvents: UnscheduledEvents;
  loadingUserCanCreateEvent: boolean;
  userCanCreateEvent: boolean;
  loadingUserCanSeeUnscheduledEvents: boolean;
  userCanSeeUnscheduledEvents: boolean;
  loadingNewEventCanHaveASecondMeetingDate: boolean;
  newEventCanHaveASecondMeetingDate: boolean;
}

export const calendarAdapter: EntityAdapter<Event> = createEntityAdapter<Event>({
  selectId: (result: Event) => result.id.toString()
});

export const initialState: CalendarState = calendarAdapter.getInitialState({
  // set initial required properties
  error: null,
  loadingEvents: false,
  loadingUnscheduledEvents: false,
  loadingOrgans: false,
  loadingNewEventOrgans: false,
  loadingAddEvent: false,
  loadingRemoveEvents: false,
  organs: null,
  newEventOrgans: null,
  viewMode: CalendarView.Month,
  hideWeekend: true,
  collapseOrgans: true,
  unscheduledEvents: {
    totalUnscheduledEventsCounter: 0
  },
  loadingUserCanCreateEvent: false,
  userCanCreateEvent: false,
  loadingUserCanSeeUnscheduledEvents: false,
  userCanSeeUnscheduledEvents: false,
  loadingNewEventCanHaveASecondMeetingDate: false,
  newEventCanHaveASecondMeetingDate: false
});

const reducer = createReducer(
  initialState,
  on(fetchEvents, (state) => ({
    ...state,
    loadingEvents: true,
    error: null
  })),
  on(fetchEventsSuccess, (state, { data }) =>
    calendarAdapter.setAll(data, {
      ...state,
      loadingEvents: false
    })
  ),
  on(fetchEventsFailure, (state, { error }) => ({ ...state, error, events: [], loadingEvents: false })),
  on(fetchUnscheduledEvents, (state) => ({
    ...state,
    loadingUnscheduledEvents: true,
    error: null
  })),
  on(fetchUnscheduledEventsSuccess, (state, { data }) => ({
    ...state,
    unscheduledEvents: data,
    loadingUnscheduledEvents: false
  })),
  on(fetchUnscheduledEventsFailure, (state, { error }) => ({
    ...state,
    error,
    unscheduledEvents: { totalUnscheduledEventsCounter: 0 },
    loadingUnscheduledEvents: false
  })),
  on(fetchOrgans, (state) => ({
    ...state,
    loadingOrgans: true,
    error: null
  })),
  on(fetchOrgansSuccess, (state, { data }) => ({
    ...state,
    loadingOrgans: false,
    organs: data,
    error: null
  })),
  on(fetchOrgansFailure, (state, { error }) => ({
    ...state,
    loadingOrgans: false,
    organs: [],
    error
  })),
  on(fetchNewEventOrgans, (state) => ({
    ...state,
    loadingNewEventOrgans: true,
    error: null
  })),
  on(fetchNewEventOrgansSuccess, (state, { data }) => ({
    ...state,
    loadingNewEventOrgans: false,
    newEventOrgans: data,
    error: null
  })),
  on(fetchNewEventOrgansFailure, (state, { error }) => ({
    ...state,
    loadingNewEventOrgans: false,
    newEventOrgans: [],
    error
  })),
  on(updateSelectedOrgansSuccess, (state, { data }) => ({
    ...state,
    organs: data,
    error: null
  })),
  on(updateSelectedOrgansFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(addEvent, (state) => ({
    ...state,
    loadingAddEvent: true,
    error: null
  })),
  on(addEventSuccess, (state, { data }) =>
    calendarAdapter.addMany(data, {
      ...state,
      loadingAddEvent: false,
      error: null
    })
  ),
  on(addEventFailure, (state, { error }) => ({
    ...state,
    loadingAddEvent: false,
    error
  })),
  on(removeEvents, (state) => ({
    ...state,
    loadingRemoveEvents: true,
    error: null
  })),
  on(removeEventsSuccess, (state, { data }) =>
    calendarAdapter.removeMany(data, {
      ...state,
      loadingRemoveEvents: false,
      error: null
    })
  ),
  on(removeEventsFailure, (state, { error }) => ({
    ...state,
    loadingRemoveEvents: false,
    error
  })),
  on(fetchUserCanCreateEvent, (state) => ({
    ...state,
    error: null
  })),
  on(fetchUserCanCreateEventSuccess, (state, { data }) => ({
    ...state,
    loadingUserCanCreateEvent: false,
    userCanCreateEvent: data,
    error: null
  })),
  on(fetchUserCanCreateEventFailure, (state, { error }) => ({
    ...state,
    loadingUserCanCreateEvent: false,
    userCanCreateEvent: false,
    error
  })),
  on(fetchUserCanSeeUnscheduledEvents, (state) => ({
    ...state,
    error: null
  })),
  on(fetchUserCanSeeUnscheduledEventsSuccess, (state, { data }) => ({
    ...state,
    loadingUserCanSeeUnscheduledEvents: false,
    userCanSeeUnscheduledEvents: data,
    error: null
  })),
  on(fetchUserCanSeeUnscheduledEventsFailure, (state, { error }) => ({
    ...state,
    userCanSeeUnscheduledEvents: false,
    loadingUserCanSeeUnscheduledEvents: false,
    error
  })),
  on(fetchNewEventCanHaveASecondMeetingDate, (state) => ({
    ...state,
    error: null
  })),
  on(fetchNewEventCanHaveASecondMeetingDateSuccess, (state, { data }) => ({
    ...state,
    loadingNewEventCanHaveASecondMeetingDate: false,
    newEventCanHaveASecondMeetingDate: data,
    error: null
  })),
  on(fetchNewEventCanHaveASecondMeetingDateFailure, (state, { error }) => ({
    ...state,
    loadingNewEventCanHaveASecondMeetingDate: false,
    newEventCanHaveASecondMeetingDate: false,
    error
  }))
);

export function calendarReducer(state: CalendarState | undefined, action: Action) {
  return reducer(state, action);
}
